import { LanguageSwitcher } from '@/i18n/LanguageSwitcher';
import { t, useUiLocale } from '@/i18n/locale';
import { BuddyEntryGuide } from '@/features/companion/BuddyEntryGuide';
import { companionEligible } from '@/features/companion/CompanionRoutes';
import { useLogout, useMe } from '@/auth/useAuth';
import { KidAvatar } from '@/components/KidAvatar';
import { SHOW_LESSONS_CATALOG } from '@/lib/features';
import { LogOut } from 'lucide-react';
import { Link } from 'react-router-dom';

// Exported for the copy-split guard. This nav points at the course-CONTENT catalog
// (课节), so its label is "Lessons"; the kid's TASK inside a lesson is a "Mission".
// `missions` survives in the route path solely as the internal route/code identifier.
// (Pre-existing data export beside the component; the fast-refresh warning is benign
// here and would otherwise fail the deploy's --max-warnings 0 lint.)
// eslint-disable-next-line react-refresh/only-export-components
export const NAV_ITEMS = [
  { to: '/learn', label: t('Home'), end: true },
  { to: '/learn/create', label: t('Create') },
  { to: '/learn/projects', label: t('Projects') },
  { to: '/learn/classroom', label: t('Classes') },
  { to: '/learn/passport', label: t('Passport') },
  { to: '/learn/workspace', label: t('AI Studio') },
  { to: '/learn/study', label: t('Study') },
  { to: '/learn/missions', label: t('Lessons') },
  { to: '/learn/hsc', label: t('HSC Plan') },
];

// The Lessons catalog is temporarily hidden (features.ts SHOW_LESSONS_CATALOG);
// NAV_ITEMS above stays the full canonical list so the copy-split guard keeps
// pinning the Lesson/Mission wording.
export const VISIBLE_NAV_ITEMS = NAV_ITEMS.filter(
  (item) => SHOW_LESSONS_CATALOG || item.to !== '/learn/missions',
);

export function LearnTopBar({ preview = false }: { preview?: boolean }) {
  useUiLocale();
  const me = useMe();
  const logout = useLogout();
  const kid = !preview && me.data?.kind === 'kid' ? me.data : null;

  return (
    <header className="relative z-10 shrink-0 border-b border-hairline bg-canvas-pure px-5 py-3 sm:px-7 xl:px-10">
      <div className="mx-auto flex w-full max-w-[1440px] items-center justify-between gap-3">
        <div className="flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-3">
          <Link
            to="/learn"
            aria-label={t('Airbotix Learn home')}
            className="shrink-0 rounded-xl focus-visible:ring-2 focus-visible:ring-brand-coral"
          >
            <img
              src="/logo-black-horizontal-approved-20261021.png"
              alt="Airbotix"
              className="h-5 w-auto sm:h-8"
            />
          </Link>
          <LanguageSwitcher />
        </div>
        {!preview && (
          <div className="flex min-w-0 items-center gap-1 sm:gap-3">
            {companionEligible(kid) && kid && <BuddyEntryGuide key={kid.sub} kidId={kid.sub} />}
            <Link
              to="/learn/profile"
              aria-label={t('My profile')}
              className="flex min-w-0 items-center gap-2 rounded-full focus-visible:ring-2 focus-visible:ring-brand-coral"
            >
              <KidAvatar avatarId={kid?.avatar_id} nickname={kid?.nickname ?? 'friend'} size="sm" />
              <span className="hidden max-w-28 truncate text-sm font-bold text-ink sm:inline">
                {kid?.nickname}
              </span>
            </Link>
            <button
              onClick={() => logout('kid', false)}
              className="btn-pill-ghost !px-3 sm:!px-5"
              aria-label={t('Sign out')}
            >
              <LogOut className="sm:hidden" size={18} aria-hidden="true" />
              <span className="hidden sm:inline">{t('Sign out')}</span>
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
